/**
 * Campus Profile API — backend calls for the Campus Profile page.
 *
 * Read:   getcampuses            (single campus details)
 *         core_corporate fetchall (corporation dropdown for BasicInfoDrawer)
 *         att_timezones fetchall  (timezone dropdown for BasicInfoDrawer)
 *         att_user_roles fetchall (impersonate role dropdown)
 *         getcampusnotes          (campus notes)
 *         getserverstatus         (server device queue data)
 *         getvendorintegrations   (active integrations)
 *         getcampusvendors        (available vendors)
 * Write:  campus_edit              (edit basic info)
 *         core_campus save POST    (settings: architecture, reporting, output)
 *         campus_careflow_settings  (settings: wellness/careflow)
 *         core_campus_notes PUT    (add note)
 *         core_campus_notes POST   (edit note)
 *         core_campus_notes DELETE  (delete notes)
 *         add_integration          (set up new vendor integration)
 *         edit_integration         (update vendor integration)
 *         toggle_integration       (activate/deactivate integration)
 *         delete_integration       (remove integration)
 *         int-onboarding           (generate/download onboarding template)
 *         int-onboard-upload       (upload onboarding file to S3)
 *
 * Phase 6 — Migrating Tab:
 * DDB:    User, Group, CampusConfig, CampusReasonCode  (DynamoDB legacy data)
 * Read:   core_user, sp_dev_getgroupsbycampusid, core_afr_entity,
 *         core_high_priority_entity, core_reason_codes, core_campus_alert_settings,
 *         att_event_types, att_inovonics_types
 * Write:  user-manage (migrate users), group_add (migrate groups),
 *         afrSave (migrate AFR), prioritySave (migrate HP alerts),
 *         core_reason_codes PUT (migrate reason codes),
 *         core_campus_alert_settings PUT + core_campus POST (migrate alert/custom settings)
 *
 * Phase 7 — Geofence + Zone Designer:
 * Read:   getgeofence2          (zone polygon data — geo1 for display, geo2 for editor)
 *         att_zone_types fetchall (zone type definitions)
 *
 * Phase 8 — Master File Upload:
 * Upload: apiUploadMasterFile    (POST /import/master with Process: "init")
 *         apiUploadFinalize      (POST /import/master with Process: "finish")
 * Read:   fetchUploadStatus      (poll stg_upload_keys for completion)
 *         fetchStagingAreas      (get_staging_areas — preview data)
 *         fetchStagingResidents  (get_staging_residents — preview data)
 *         fetchStagingDevices    (get_staging_devices — preview data)
 */

import { apiFetch, apiSave, apiTransaction, apiQueryItems, apiUserManageLegacy } from '../../../../../services/api.js';

// ---------------------------------------------------------------------------
// READ operations
// ---------------------------------------------------------------------------

/**
 * Fetch a single campus by ID.
 *
 * V1 procedure: getcampuses with campus_filter
 * Returns array — caller should use result[0] for the single record.
 */
export function fetchCampusProfile(campusId, targetCampusId) {
  return apiFetch({
    procedure: 'getcampuses',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    payload: { campus_filter: targetCampusId.toString() },
    readOnly: true,
  });
}

/**
 * Fetch all corporations (for the BasicInfoDrawer corporation dropdown).
 *
 * V1: Tbl: "core_corporate", procedure: "fetchall", reqType: "direct"
 */
export function fetchCorporations(campusId) {
  return apiFetch({
    procedure: 'fetchall',
    reqType: 'direct',
    table: 'core_corporate',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

/**
 * Fetch all timezones (for the BasicInfoDrawer timezone dropdown).
 *
 * V1: Tbl: "att_timezones", procedure: "fetchall", reqType: "direct"
 */
export function fetchTimezones(campusId) {
  return apiFetch({
    procedure: 'fetchall',
    reqType: 'direct',
    table: 'att_timezones',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

/**
 * Fetch all user roles (for the Impersonate role dropdown).
 *
 * V1: Tbl: "att_user_roles", procedure: "fetchall", reqType: "direct"
 */
export function fetchUserRoles(campusId) {
  return apiFetch({
    procedure: 'fetchall',
    reqType: 'direct',
    table: 'att_user_roles',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

// ---------------------------------------------------------------------------
// WRITE operations
// ---------------------------------------------------------------------------

/**
 * Save edited campus basic info.
 *
 * V1: mytransaction with TransType: "campus_edit"
 *
 * @param {Array} payload    — [{ campus_id, corporate_id, campus_country, campus_state, campus_name,
 *                                campus_city, campus_zip, campus_email, campus_phone, timezone_id,
 *                                modified_by, campus_street_address }]
 * @param {Array} oldPayload — same shape for audit trail (without modified_by)
 * @param {object} session   — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export function editCampusBasicInfo(payload, oldPayload, session) {
  return apiTransaction({
    transType: 'campus_edit',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Save campus settings (Architecture, Event Reporting, Output Features).
 *
 * V1: mysave to core_campus, PKey: campus_id, method: POST
 * Used for all three non-careflow settings groups.
 *
 * @param {Array} payload    — [{ campus_id, ...setting fields, modified_by }]
 * @param {Array} oldPayload — [{ campus_id, ...original setting fields }]
 * @param {object} session   — { campusId, userId, clientId }
 */
export function saveCampusSettings(payload, oldPayload, session) {
  return apiSave({
    table: 'core_campus',
    payload,
    oldPayload,
    pKey: 'campus_id',
    condition: 'primary',
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
    method: 'POST',
  });
}

/**
 * Save careflow/wellness settings.
 *
 * V1: mytransaction with TransType: "campus_careflow_settings"
 * Uses transaction instead of direct save because careflow changes
 * may trigger additional backend side-effects.
 *
 * @param {Array} payload    — [{ campus_id, careflow_v2, use_area_careflow, ... }]
 * @param {Array} oldPayload — [{ campus_id, ... original values }]
 * @param {object} session   — { campusId, userId, clientId }
 */
export function saveCareflowSettings(payload, oldPayload, session) {
  return apiTransaction({
    transType: 'campus_careflow_settings',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

// ---------------------------------------------------------------------------
// Phase 3 — Notes
// ---------------------------------------------------------------------------

/**
 * Fetch all campus notes.
 *
 * V1: getcampusnotes stored procedure
 */
export function fetchCampusNotes(campusId, targetCampusId) {
  return apiFetch({
    procedure: 'getcampusnotes',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    payload: { campus_id: targetCampusId },
    readOnly: false,
  });
}

/**
 * Add a new campus note.
 *
 * V1: mysave to core_campus_notes, method: PUT
 */
export function addCampusNote(payload, session) {
  return apiSave({
    table: 'core_campus_notes',
    payload,
    pKey: 'note_id',
    condition: 'primary',
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
    method: 'PUT',
  });
}

/**
 * Edit an existing campus note.
 *
 * V1: mysave to core_campus_notes, method: POST
 */
export function editCampusNote(payload, session) {
  return apiSave({
    table: 'core_campus_notes',
    payload,
    pKey: 'note_id',
    condition: 'primary',
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
    method: 'POST',
  });
}

/**
 * Delete campus notes (bulk).
 *
 * V1: mysave to core_campus_notes, method: DELETE
 */
export function deleteCampusNotes(payload, session) {
  return apiSave({
    table: 'core_campus_notes',
    payload,
    pKey: 'note_id',
    condition: 'primary',
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
    method: 'DELETE',
  });
}

// ---------------------------------------------------------------------------
// Phase 4 — Server Info
// ---------------------------------------------------------------------------

/**
 * Fetch server status/queue data for this campus.
 *
 * V1: getserverstatus stored procedure
 */
export function fetchServerStatus(campusId) {
  return apiFetch({
    procedure: 'getserverstatus',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

// ---------------------------------------------------------------------------
// Phase 5 — Integrations
// ---------------------------------------------------------------------------

/**
 * Fetch active vendor integrations for this campus.
 *
 * V1: getvendorintegrations stored procedure
 */
export function fetchVendorIntegrations(campusId) {
  return apiFetch({
    procedure: 'getvendorintegrations',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: false,
  });
}

/**
 * Fetch all available campus vendors.
 *
 * V1: getcampusvendors stored procedure
 */
export function fetchCampusVendors(campusId) {
  return apiFetch({
    procedure: 'getcampusvendors',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: false,
  });
}

/**
 * Set up a new vendor integration.
 *
 * V1: mytransaction TransType: "add_integration"
 */
export function addIntegration(payload, session) {
  return apiTransaction({
    transType: 'add_integration',
    payload,
    oldPayload: [],
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Edit an existing vendor integration.
 *
 * V1: mytransaction TransType: "edit_integration"
 */
export function editIntegration(payload, oldPayload, session) {
  return apiTransaction({
    transType: 'edit_integration',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Activate or deactivate a vendor integration.
 *
 * V1: mytransaction TransType: "toggle_integration"
 */
export function toggleIntegration(payload, oldPayload, session) {
  return apiTransaction({
    transType: 'toggle_integration',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Delete a vendor integration.
 *
 * V1: mytransaction TransType: "delete_integration"
 */
export function deleteIntegration(payload, session) {
  return apiTransaction({
    transType: 'delete_integration',
    payload,
    oldPayload: [],
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

// ---------------------------------------------------------------------------
// Phase 5 — Onboarding (custom S3 endpoints)
// ---------------------------------------------------------------------------

const getBaseUrl = () =>
  import.meta.env.VITE_DEVMODE === 'true'
    ? import.meta.env.VITE_DEVAPI
    : import.meta.env.VITE_API;

const getHeaders = () => ({
  'Content-Type': 'application/json',
  'x-amz-security-token': import.meta.env.VITE_AUTH_TOKEN,
});

/**
 * Call the int-onboarding endpoint (generate control ID, create spreadsheet, get presigned URL).
 *
 * V1: ApiRequester({reqEndpoint: "int-onboarding", ...})
 */
export async function apiIntOnboarding(params) {
  const payload = {
    action: params.action,
    campus_id: params.campusId,
    client_id: params.clientId,
    data_type: params.dataType || 'all',
    vendor_id: params.vendorId,
    s3_bucket: 'palcare-ehr-exports' + (import.meta.env.VITE_DEVMODE === 'true' ? '-dev' : '-prod'),
    s3_key_prefix: params.s3KeyPrefix,
    async_processing: params.async,
    file_id: params.fileId,
    corporate_id: params.corporateId,
    user_id: params.userId,
  };
  try {
    const resp = await fetch(getBaseUrl() + import.meta.env.VITE_INT_ONBOARD_ENDPOINT, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(payload),
    });
    return await resp.json();
  } catch (err) {
    console.error('[apiIntOnboarding] error:', err);
    return [false, 'Request failed.'];
  }
}

/**
 * Call the int-onboard-upload endpoint (get upload URL, process upload).
 *
 * V1: ApiRequester({reqEndpoint: "int-onboard-upload", ...})
 */
export async function apiIntOnboardUpload(params) {
  const payload = {
    action: params.action,
    campus_id: params.campusId,
    onboarding_type: params.type || 'EHR',
    data_type: params.dataType || 'all',
    vendor_id: params.vendorId,
    async_processing: params.async,
    upload_id: params.uploadId,
    s3_bucket: params.s3Bucket,
    s3_key: params.s3Key,
    corporate_id: params.corporateId,
    user_id: params.userId,
  };
  try {
    const resp = await fetch(getBaseUrl() + import.meta.env.VITE_INT_UPLOAD_ENDPOINT, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(payload),
    });
    return await resp.json();
  } catch (err) {
    console.error('[apiIntOnboardUpload] error:', err);
    return [false, 'Request failed.'];
  }
}

/**
 * Poll ext_ehr_file_control for onboarding completion status.
 *
 * V1: myfetch with fetchbyparameter on ext_ehr_file_control
 */
export function fetchOnboardStatus(campusId, fileId) {
  return apiFetch({
    procedure: 'fetchbyparameter',
    reqType: 'direct',
    table: 'ext_ehr_file_control',
    pKey: 'file_id',
    pVal: fileId,
    readOnly: false,
  });
}

// ===========================================================================
// Phase 6 — Migrating Tab
// ===========================================================================

// ---------------------------------------------------------------------------
// DynamoDB reads (legacy 2.0 data)
// ---------------------------------------------------------------------------

/** Fetch 2.0 Users from DynamoDB User table */
export function fetchDDBUsers(campusGuid, campusId) {
  return apiQueryItems({
    table: 'User',
    partitionKey: 'CampusId',
    partitionValue: campusGuid,
    campusId,
    secondIndex: 'CampusId-index',
    projection: 'UserId,DisplayName,Email,UserName,UserRole,GroupIds',
  });
}

/** Fetch 2.0 Groups from DynamoDB Group table */
export function fetchDDBGroups(campusGuid, campusId) {
  return apiQueryItems({
    table: 'Group',
    partitionKey: 'CampusId',
    partitionValue: campusGuid,
    campusId,
    secondIndex: 'CampusId-index',
    projection: 'GroupId,CapCode,GroupName',
  });
}

/** Fetch 2.0 AFR Devices from DynamoDB CampusConfig table */
export function fetchDDBAFR(campusGuid, campusId) {
  return apiQueryItems({
    table: 'CampusConfig',
    partitionKey: 'CampusId',
    partitionValue: campusGuid,
    campusId,
    projection: 'AFRDevices',
  });
}

/** Fetch 2.0 High Priority Alert Types from DynamoDB CampusConfig table */
export function fetchDDBHP(campusGuid, campusId) {
  return apiQueryItems({
    table: 'CampusConfig',
    partitionKey: 'CampusId',
    partitionValue: campusGuid,
    campusId,
    projection: 'HighPriorityAlertTypes',
  });
}

/** Fetch 2.0 Alert & Customization settings from DynamoDB CampusConfig table */
export function fetchDDBAlertSettings(campusGuid, campusId) {
  return apiQueryItems({
    table: 'CampusConfig',
    partitionKey: 'CampusId',
    partitionValue: campusGuid,
    campusId,
    projection: 'FinishButtonEnabled,FinishTimeInterval,ReleaseTimeInterval,AcceptButtonEnabled,ReasonCodeEnabled,CaregiverChgGroupEnabled,UserEditDisplayName,ClearTimeInterval',
  });
}

/** Fetch 2.0 Reason Codes from DynamoDB CampusReasonCode table */
export function fetchDDBReasonCodes(campusGuid, campusId) {
  return apiQueryItems({
    table: 'CampusReasonCode',
    partitionKey: 'CampusId',
    partitionValue: campusGuid,
    campusId,
    projection: 'ReasonCodeId,Description,GlobalReasonCode',
  });
}

// ---------------------------------------------------------------------------
// 4.0 SQL reads (existing data for comparison)
// ---------------------------------------------------------------------------

/** Fetch 4.0 users for comparison — core_user by campus_id */
export function fetchMyUsers(campusId) {
  return apiFetch({
    procedure: 'fetchbyparameter',
    reqType: 'direct',
    table: 'core_user',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: false,
  });
}

/** Fetch 4.0 groups — sp_dev_getgroupsbycampusid */
export function fetchMyGroups(campusId) {
  return apiFetch({
    procedure: 'sp_dev_getgroupsbycampusid',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: false,
  });
}

/** Fetch 4.0 AFR entities — core_afr_entity by campus_id */
export function fetchMyAFR(campusId) {
  return apiFetch({
    procedure: 'fetchbyparameter',
    reqType: 'direct',
    table: 'core_afr_entity',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: false,
  });
}

/** Fetch 4.0 high priority types — gethighprioritytypes */
export function fetchMyHP(campusId) {
  return apiFetch({
    procedure: 'gethighprioritytypes',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: false,
  });
}

/** Fetch 4.0 reason codes — core_reason_codes by campus_id */
export function fetchMyReasonCodes(campusId) {
  return apiFetch({
    procedure: 'fetchbyparameter',
    reqType: 'direct',
    table: 'core_reason_codes',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: false,
  });
}

/** Fetch 4.0 alert settings — core_campus_alert_settings by campus_id */
export function fetchMyAlertSettings(campusId) {
  return apiFetch({
    procedure: 'fetchbyparameter',
    reqType: 'direct',
    table: 'core_campus_alert_settings',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: false,
  });
}

// ---------------------------------------------------------------------------
// Lookup tables
// ---------------------------------------------------------------------------

/** Fetch event types (for HP alert matching) — att_event_types fetchall */
export function fetchEventTypes(campusId) {
  return apiFetch({
    procedure: 'fetchall',
    reqType: 'direct',
    table: 'att_event_types',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

/** Fetch device types (for AFR matching) — att_inovonics_types fetchall */
export function fetchDeviceTypes(campusId) {
  return apiFetch({
    procedure: 'fetchall',
    reqType: 'direct',
    table: 'att_inovonics_types',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

// ---------------------------------------------------------------------------
// Migration submit operations
// ---------------------------------------------------------------------------

/** Migrate users via legacy user-manage endpoint */
export function migrateUsers(payload, userId) {
  return apiUserManageLegacy({ payload, userId });
}

/** Migrate groups via group_add transaction */
export function migrateGroups(payload, session) {
  return apiTransaction({
    transType: 'group_add',
    payload,
    oldPayload: [],
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/** Migrate AFR devices via afrSave transaction */
export function migrateAFRDevices(payload, oldPayload, session) {
  return apiTransaction({
    transType: 'afrSave',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/** Migrate HP alert types via prioritySave transaction */
export function migrateHPAlerts(payload, oldPayload, session) {
  return apiTransaction({
    transType: 'prioritySave',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/** Migrate reason codes via core_reason_codes PUT (create) */
export function migrateReasonCodes(payload, session) {
  return apiSave({
    table: 'core_reason_codes',
    payload,
    pKey: 'cloud_reason_code_id',
    condition: 'primary',
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
    method: 'PUT',
  });
}

/** Migrate alert settings — core_campus_alert_settings PUT (create) */
export function migrateAlertSettings(payload, session) {
  return apiSave({
    table: 'core_campus_alert_settings',
    payload,
    pKey: 'alert_setting_id',
    condition: 'primary',
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
    method: 'PUT',
  });
}

/** Migrate custom settings — core_campus POST (update is_name_editable, is_group_editable) */
export function migrateCustomSettings(payload, session) {
  return apiSave({
    table: 'core_campus',
    payload,
    pKey: 'campus_id',
    condition: 'primary',
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
    method: 'POST',
  });
}

// ===========================================================================
// Phase 7 — Geofence + Zone Designer
// ===========================================================================

/**
 * Fetch geofence zone data for the read-only map.
 *
 * V1: getgeofence2 stored procedure — returns { geo1, geo2 }.
 *   geo1 = zone objects keyed by zone_type_name (for read-only polygon rendering)
 *   geo2 = flat array of zone objects (for GeofenceTool editing)
 */
export function fetchGeofence(campusId) {
  return apiFetch({
    procedure: 'getgeofence2',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    payload: { campus_id: campusId },
    readOnly: false,
  });
}

/**
 * Fetch zone type definitions (for GeofenceTool dropdown).
 *
 * V1: att_zone_types fetchall direct
 */
export function fetchZoneTypes(campusId) {
  return apiFetch({
    procedure: 'fetchall',
    reqType: 'direct',
    table: 'att_zone_types',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

// ===========================================================================
// Phase 8 — Master File Upload
// ===========================================================================

/**
 * Initiate master file upload — sends CSV to backend for AWS Lambda processing.
 *
 * V1: ApiRequester({ reqEndpoint: "uploadprocess", Process: "init" })
 * Returns an upload key (string) on success, used to track status.
 *
 * @param {object} params
 * @param {string} params.uploadFile — base64-encoded CSV content
 * @param {number} params.campusId   — campus receiving the upload
 * @param {string} params.userId     — current user ID
 * @returns {Promise<*>} upload key or error
 */
export async function apiUploadMasterFile({ uploadFile, campusId, userId }) {
  const payload = {
    Content: {
      upload_file: uploadFile,
      upload_areas: true,
      upload_residents: true,
      upload_devices: true,
    },
    CampusId: campusId,
    ClientId: null,
    Process: 'init',
    User: userId,
  };
  try {
    const resp = await fetch(getBaseUrl() + import.meta.env.VITE_UPLOAD_ENDPOINT, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(payload),
    });
    return await resp.json();
  } catch (err) {
    console.error('[apiUploadMasterFile] error:', err);
    return [false, 'Upload request failed.'];
  }
}

/**
 * Poll stg_upload_keys table for upload/submit completion status.
 *
 * V1: ApiRequester({ reqEndpoint: "myfetch", Tbl: "stg_upload_keys",
 *                    procedure: "fetchbyparameter", pKey: "unique_key" })
 *
 * Response row fields:
 *   upload_complete (1 = file processed)
 *   upload_error    (1 = processing failed)
 *   submit_complete (1 = finalization done)
 *   submit_error    (1 = finalization failed)
 *
 * @param {string} uploadKey — unique key from apiUploadMasterFile
 * @returns {Promise<Array>} array of status rows (expect 1)
 */
export function fetchUploadStatus(uploadKey) {
  return apiFetch({
    procedure: 'fetchbyparameter',
    reqType: 'direct',
    table: 'stg_upload_keys',
    pKey: 'unique_key',
    pVal: uploadKey,
    readOnly: false,
  });
}

/**
 * Finalize master file upload — commits staging data to production.
 *
 * V1: ApiRequester({ reqEndpoint: "uploadprocess", Process: "finish" })
 *
 * @param {object} params
 * @param {string} params.uploadKey — unique key identifying the staging session
 * @param {number} params.campusId  — campus receiving the upload
 * @param {string} params.userId    — current user ID
 * @returns {Promise<*>}
 */
export async function apiUploadFinalize({ uploadKey, campusId, userId }) {
  const payload = {
    Content: { file_key: uploadKey },
    CampusId: campusId,
    ClientId: null,
    Process: 'finish',
    User: userId,
  };
  try {
    const resp = await fetch(getBaseUrl() + import.meta.env.VITE_UPLOAD_ENDPOINT, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(payload),
    });
    return await resp.json();
  } catch (err) {
    console.error('[apiUploadFinalize] error:', err);
    return [false, 'Finalization request failed.'];
  }
}

/**
 * Fetch staged areas for upload preview.
 *
 * V1: ApiRequester({ procedure: "get_staging_areas", reqType: "stored" })
 */
export function fetchStagingAreas(campusId) {
  return apiFetch({
    procedure: 'get_staging_areas',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: false,
  });
}

/**
 * Fetch staged residents for upload preview.
 *
 * V1: ApiRequester({ procedure: "get_staging_residents", reqType: "stored" })
 */
export function fetchStagingResidents(campusId) {
  return apiFetch({
    procedure: 'get_staging_residents',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: false,
  });
}

/**
 * Fetch staged devices for upload preview.
 *
 * V1: ApiRequester({ procedure: "get_staging_devices", reqType: "stored" })
 */
export function fetchStagingDevices(campusId) {
  return apiFetch({
    procedure: 'get_staging_devices',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: false,
  });
}
